import React from 'react';
import { WizardStep } from '../../types/wizard';

interface StepIndicatorProps {
  steps: WizardStep[];
  currentStepId: string;
  onStepClick?: (stepId: string) => void;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ steps, currentStepId, onStepClick }) => {
  return (
    <>
      {/* Desktop: Horizontal stepper */}
      <div className="hidden md:block mb-8 rounded-2xl border border-gray-200 bg-white px-6 py-5 shadow-sm">
        <div className="flex items-start justify-between">
          {steps.map((step, index) => {
            // Option A: Lock all steps once submitted - no backward navigation
            const isClickable = false;
            const isLast = index === steps.length - 1;

            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center flex-1">
                  {/* Step circle */}
                  <button
                    onClick={() => isClickable && onStepClick && onStepClick(step.id)}
                    disabled={!isClickable}
                    className={`
                      w-10 h-10 rounded-xl flex items-center justify-center text-base font-semibold
                      transition-all duration-200 mb-2
                      ${step.status === 'completed'
                        ? 'bg-logo-green text-white cursor-default'
                        : step.status === 'current'
                        ? 'bg-logo-green text-white ring-4 ring-logo-green/20 cursor-default shadow-md'
                        : step.status === 'pending'
                        ? 'bg-gray-100 text-slate cursor-default'
                        : 'bg-gray-100 text-gray-400 cursor-default'}
                    `}
                  >
                    {step.icon}
                  </button>

                  {/* Step title */}
                  <div className="text-center">
                    <div className={`
                      text-xs font-semibold max-w-[7rem]
                      ${step.status === 'completed' || step.status === 'current'
                        ? 'text-charcoal'
                        : 'text-gray-400'}
                    `}>
                      {step.title}
                    </div>
                    {step.status === 'current' && (
                      <div className="text-xs text-logo-green mt-1">
                        Current
                      </div>
                    )}
                  </div>
                </div>

                {/* Connector line */}
                {!isLast && (
                  <div className={`
                    flex-1 h-0.5 mx-2 mt-5
                    ${step.status === 'completed'
                      ? 'bg-logo-green'
                      : 'bg-gray-200'}
                  `} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Mobile: Vertical dots with current step */}
      <div className="md:hidden mb-6">
        <div className="card p-4">
          {/* Current step info */}
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 rounded-xl bg-logo-green text-white flex items-center justify-center text-xl mr-3 shadow-sm">
              {steps.find(s => s.id === currentStepId)?.icon}
            </div>
            <div className="flex-1">
              <div className="font-semibold text-charcoal">
                {steps.find(s => s.id === currentStepId)?.title}
              </div>
              <div className="text-sm text-slate">
                Step {steps.findIndex(s => s.id === currentStepId) + 1} of {steps.length}
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className="bg-logo-green h-2 rounded-full transition-all duration-500"
              style={{
                width: `${((steps.findIndex(s => s.id === currentStepId) + 1) / steps.length) * 100}%`
              }}
            />
          </div>

          {/* Step count dots */}
          <div className="flex justify-center mt-4 space-x-1">
            {steps.map((step) => (
              <button
                key={step.id}
                onClick={() => (step.status === 'completed' || step.status === 'current') && onStepClick && onStepClick(step.id)}
                disabled={step.status !== 'completed' && step.status !== 'current'}
                className={`
                  w-2 h-2 rounded-full transition-all duration-200
                  ${step.status === 'completed'
                    ? 'bg-logo-green'
                    : step.status === 'current'
                    ? 'bg-logo-green w-6'
                    : 'bg-gray-300'}
                `}
                title={step.title}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default StepIndicator;
